import React, { useEffect, useState } from "react";
import { employees, getEmployeeName } from "../../Config/Employees";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsTableFrame } from "../../components/Hrms/HrmsTable";
import { PersonCell } from "../../components/Hrms/hrmsUi";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { validateExitDates } from "./hrmsDateRules";

export const EmployeeExit = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [exitRecords, setExitRecords] = useState<any[]>([]);
  const [myExit, setMyExit] = useState<any>(null);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterClearance, setFilterClearance] = useState("");

  // Initiate exit modal (admin)
  const {
    isOpen: isInitiateOpen,
    onOpen: onInitiateOpen,
    onClose: onInitiateClose,
  } = useDisclosure();
  const [newExit, setNewExit] = useState({
    email: "",
    resignationDate: "",
    lastWorkingDay: "",
    adminNotes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Update exit modal (admin)
  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [updateData, setUpdateData] = useState({
    lastWorkingDay: "",
    handoverSubmitted: false,
    pendingTasks: true,
    pendingTaskDetails: "",
    finalSalaryStatus: "on_hold",
    finalSalaryHoldReason: "",
    relievingLetterIssued: false,
    relievingLetterDate: "",
    adminNotes: "",
  });

  // Grant clearance modal (admin)
  const {
    isOpen: isClearanceOpen,
    onOpen: onClearanceOpen,
    onClose: onClearanceClose,
  } = useDisclosure();
  const [clearanceNotes, setClearanceNotes] = useState("");


  const getStatusBadge = (value: any) => {
    if (typeof value === "boolean") {
      return (
        <Badge colorScheme={value ? "green" : "orange"}>
          {value ? "Yes" : "No"}
        </Badge>
      );
    }
    const colorMap: any = {
      pending: "orange",
      completed: "green",
      on_hold: "red",
      released: "green",
    };
    return (
      <Badge colorScheme={colorMap[value] || "gray"}>
        {value?.replace(/_/g, " ") || "N/A"}
      </Badge>
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return DateTime.fromISO(dateStr).toFormat("dd MMM yyyy");
  };

  const fetchExitRecords = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getExitRecords(
        token,
        user,
        isAdmin ? filterEmail : ""
      );
      if (res.data?.data) {
        if (isAdmin) {
          setExitRecords(res.data.data);
        } else {
          const mine = res.data.data.find((r: any) => r.email === userEmail);
          setMyExit(mine || null);
          setExitRecords(res.data.data);
        }
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitiateExit = async () => {
    if (!newExit.email || !newExit.resignationDate || !newExit.lastWorkingDay) {
      toast({
        position: "top",
        title: "Please fill all required fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const dateError = validateExitDates(newExit.resignationDate, newExit.lastWorkingDay);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    try {
      await EmployeeDataService.initiateExit(token, { ...newExit, name: getEmployeeName(newExit.email) }, user);
      toast({
        position: "top",
        title: "Exit process initiated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onInitiateClose();
      setNewExit({ email: "", resignationDate: "", lastWorkingDay: "", adminNotes: "" });
      fetchExitRecords();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to initiate exit",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleUpdateExit = async () => {
    if (!selectedRecord) return;
    const dateError = validateExitDates(
      selectedRecord.resignationDate,
      updateData.lastWorkingDay || selectedRecord.lastWorkingDay,
      updateData.relievingLetterDate,
    );
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }
    try {
      await EmployeeDataService.updateExit(
        token,
        selectedRecord._id,
        updateData,
        user
      );
      toast({
        position: "top",
        title: "Exit record updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onUpdateClose();
      fetchExitRecords();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleGrantClearance = async () => {
    if (!selectedRecord) return;
    try {
      await EmployeeDataService.grantClearance(
        token,
        selectedRecord._id,
        { clearanceNotes, exitClearance: "completed" },
        user
      );
      toast({
        position: "top",
        title: "Clearance granted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onClearanceClose();
      setClearanceNotes("");
      fetchExitRecords();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to grant clearance",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchExitRecords();
  }, []);

  return (
    <HrmsPageShell
      title="Exit Management"
      description="Resignations, handover and final settlement"
      sectionKey="me"
      isLoading={isLoading}
    >
      <>
        {/* Exit Policy Info Card */}
        <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" p={5} mb={6}>
          <Text fontWeight="bold" color="blue.700" mb={2}>
            Exit Policy
          </Text>
          <Text fontSize="sm" color="blue.600" m={0} mb={1}>
            - Mandatory handover document submission before last working day.
          </Text>
          <Text fontSize="sm" color="blue.600" m={0} mb={1}>
            - Relieving letter and final salary will be processed within 45 days from last working day.
          </Text>
          <Text fontSize="sm" color="red.500" fontWeight="semibold" m={0}>
            - If handover is not completed, final salary will be delayed.
          </Text>
        </Box>

        {/* Employee View — Own Exit Status */}
        {!isAdmin && (
          <>
            {myExit ? (
              <Box bg="white" borderWidth="1px" borderRadius="md" p={6} mb={6}>
                <Text fontWeight="bold" fontSize="lg" mb={4}>
                  Your Exit Status
                </Text>
                <Flex gap={8} flexWrap="wrap">
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Resignation Date</Text>
                    <Text fontWeight="semibold" m={0}>{formatDate(myExit.resignationDate)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Last Working Day</Text>
                    <Text fontWeight="semibold" m={0}>{formatDate(myExit.lastWorkingDay)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Handover Submitted</Text>
                    {getStatusBadge(myExit.handoverSubmitted)}
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Pending Tasks</Text>
                    {getStatusBadge(myExit.pendingTasks)}
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Exit Clearance</Text>
                    {getStatusBadge(myExit.exitClearance || "pending")}
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" m={0}>Final Salary</Text>
                    {getStatusBadge(myExit.finalSalaryStatus || "on_hold")}
                  </Box>
                  {myExit.finalSalaryStatus === "on_hold" && myExit.finalSalaryHoldReason && (
                    <Box>
                      <Text fontSize="sm" color="gray.500" m={0}>Hold Reason</Text>
                      <Text fontSize="sm" color="red.500" fontWeight="semibold" m={0}>{myExit.finalSalaryHoldReason}</Text>
                    </Box>
                  )}
                </Flex>

              </Box>
            ) : (
              <Box bg="white" borderWidth="1px" borderRadius="md" p={6} textAlign="center" color="gray.500">
                <Text>No active exit process found for your account.</Text>
              </Box>
            )}
          </>
        )}

        {/* Admin View */}
        {isAdmin && (
          <>
            {/* Admin Controls */}
            <Flex gap={4} mb={6} align="flex-end" flexWrap="wrap">
              <Button bg="#327171" color="white" onClick={onInitiateOpen} _hover={{ bg: "#285e5e" }}>
                Initiate Exit Process
              </Button>
              <FormControl w="200px">
                <FormLabel fontSize="sm">Employee</FormLabel>
                <Select
                  placeholder="All Employees"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                >
                  {employees.map((emp) => (
                    <option key={emp.value} value={emp.value}>{emp.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl w="160px">
                <FormLabel fontSize="sm">Clearance</FormLabel>
                <Select
                  placeholder="All"
                  value={filterClearance}
                  onChange={(e) => setFilterClearance(e.target.value)}
                >
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </Select>
              </FormControl>
              <Button bg="#327171" color="white" onClick={fetchExitRecords} _hover={{ bg: "#285e5e" }}>
                Search
              </Button>
            </Flex>

            {/* Exit Records Table */}
            <HrmsTableFrame>
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Resignation Date</th>
                    <th>Last Working Day</th>
                    <th>Handover</th>
                    <th>Pending Tasks</th>
                    <th>Clearance</th>
                    <th>Final Salary</th>
                    <th>Relieving Letter</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exitRecords
                    .filter((r: any) => !filterClearance || r.exitClearance === filterClearance)
                    .length > 0 ? (
                    exitRecords
                      .filter((r: any) => !filterClearance || r.exitClearance === filterClearance)
                      .map((r: any) => (
                        <tr key={r._id}>
                          <td>
                            <PersonCell name={r.name || r.email} subtitle={r.email} />
                          </td>
                          <td>{formatDate(r.resignationDate)}</td>
                          <td>{formatDate(r.lastWorkingDay)}</td>
                          <td>{getStatusBadge(r.handoverSubmitted)}</td>
                          <td>{getStatusBadge(r.pendingTasks)}</td>
                          <td>{getStatusBadge(r.exitClearance || "pending")}</td>
                          <td>
                            {getStatusBadge(r.finalSalaryStatus || "on_hold")}
                            {r.finalSalaryStatus === "on_hold" && r.finalSalaryHoldReason && (
                              <Text fontSize="xs" color="red.500" m={0}>{r.finalSalaryHoldReason}</Text>
                            )}
                          </td>
                          <td>{getStatusBadge(r.relievingLetterIssued)}</td>
                          <td>
                            <Flex gap={2} flexWrap="wrap">
                              <Button
                                size="xs"
                                bg="#327171"
                                color="white"
                                _hover={{ bg: "#285e5e" }}
                                onClick={() => {
                                  setSelectedRecord(r);
                                  setUpdateData({
                                    lastWorkingDay: r.lastWorkingDay || "",
                                    handoverSubmitted: r.handoverSubmitted ?? false,
                                    pendingTasks: r.pendingTasks ?? true,
                                    pendingTaskDetails: r.pendingTaskDetails || "",
                                    finalSalaryStatus: r.finalSalaryStatus || "on_hold",
                                    finalSalaryHoldReason: r.finalSalaryHoldReason || "",
                                    relievingLetterIssued: r.relievingLetterIssued ?? false,
                                    relievingLetterDate: r.relievingLetterDate || "",
                                    adminNotes: r.adminNotes || "",
                                  });
                                  onUpdateOpen();
                                }}
                              >
                                Update
                              </Button>
                              {r.exitClearance !== "completed" && (
                                <Button
                                  size="xs"
                                  colorScheme="green"
                                  onClick={() => {
                                    setSelectedRecord(r);
                                    setClearanceNotes("");
                                    onClearanceOpen();
                                  }}
                                >
                                  Clearance
                                </Button>
                              )}
                            </Flex>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center", color: "#a0aec0" }}>
                        No exit records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </HrmsTableFrame>

            {/* Initiate Exit Modal */}
            <Modal isOpen={isInitiateOpen} onClose={onInitiateClose} isCentered>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Initiate Exit Process</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <FormControl mb={3} isRequired>
                    <FormLabel>Employee</FormLabel>
                    <Select
                      placeholder="Select Employee"
                      value={newExit.email}
                      onChange={(e) => setNewExit({ ...newExit, email: e.target.value })}
                    >
                      {employees.map((emp) => (
                        <option key={emp.value} value={emp.value}>{emp.label}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl mb={3} isRequired>
                    <FormLabel>Resignation Date</FormLabel>
                    <Input
                      type="date"
                      value={newExit.resignationDate}
                      onChange={(e) => setNewExit({ ...newExit, resignationDate: e.target.value })}
                    />
                  </FormControl>
                  <FormControl mb={3} isRequired>
                    <FormLabel>Last Working Day</FormLabel>
                    <Input
                      type="date"
                      value={newExit.lastWorkingDay}
                      min={newExit.resignationDate || undefined}
                      onChange={(e) => setNewExit({ ...newExit, lastWorkingDay: e.target.value })}
                    />
                  </FormControl>
                  <FormControl mb={3}>
                    <FormLabel>Admin Notes</FormLabel>
                    <Textarea
                      value={newExit.adminNotes}
                      onChange={(e) => setNewExit({ ...newExit, adminNotes: e.target.value })}
                      placeholder="Notes..."
                    />
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button mr={3} onClick={onInitiateClose}>Cancel</Button>
                  <Button
                    bg="#327171"
                    color="white"
                    onClick={handleInitiateExit}
                    isLoading={isSubmitting}
                    _hover={{ bg: "#285e5e" }}
                  >
                    Initiate
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            {/* Update Exit Modal */}
            <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} isCentered size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Update Exit Details</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {selectedRecord && (
                    <Text mb={3}>
                      <strong>{selectedRecord.name || selectedRecord.email}</strong>
                    </Text>
                  )}
                  <FormControl mb={3}>
                    <FormLabel>Last Working Day</FormLabel>
                    <Input
                      type="date"
                      value={updateData.lastWorkingDay}
                      onChange={(e) => setUpdateData({ ...updateData, lastWorkingDay: e.target.value })}
                    />
                  </FormControl>
                  <FormControl mb={3}>
                    <FormLabel>Handover Submitted</FormLabel>
                    <Select
                      value={updateData.handoverSubmitted ? "true" : "false"}
                      onChange={(e) => setUpdateData({ ...updateData, handoverSubmitted: e.target.value === "true" })}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Select>
                  </FormControl>
                  <FormControl mb={3}>
                    <FormLabel>Pending Tasks</FormLabel>
                    <Select
                      value={updateData.pendingTasks ? "true" : "false"}
                      onChange={(e) => setUpdateData({ ...updateData, pendingTasks: e.target.value === "true" })}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Select>
                  </FormControl>
                  {updateData.pendingTasks && (
                    <FormControl mb={3}>
                      <FormLabel>Pending Task Details</FormLabel>
                      <Textarea
                        value={updateData.pendingTaskDetails}
                        onChange={(e) => setUpdateData({ ...updateData, pendingTaskDetails: e.target.value })}
                        placeholder="Details of pending tasks..."
                      />
                    </FormControl>
                  )}
                  <FormControl mb={3}>
                    <FormLabel>Final Salary Status</FormLabel>
                    <Select
                      value={updateData.finalSalaryStatus}
                      onChange={(e) => setUpdateData({ ...updateData, finalSalaryStatus: e.target.value })}
                    >
                      <option value="on_hold">On Hold</option>
                      <option value="pending">Pending</option>
                      <option value="released">Released</option>
                    </Select>
                  </FormControl>
                  {updateData.finalSalaryStatus === "on_hold" && (
                    <FormControl mb={3}>
                      <FormLabel>Hold Reason</FormLabel>
                      <Textarea
                        value={updateData.finalSalaryHoldReason}
                        onChange={(e) => setUpdateData({ ...updateData, finalSalaryHoldReason: e.target.value })}
                        placeholder="Reason for holding salary..."
                      />
                    </FormControl>
                  )}
                  <FormControl mb={3}>
                    <FormLabel>Relieving Letter Issued</FormLabel>
                    <Select
                      value={updateData.relievingLetterIssued ? "true" : "false"}
                      onChange={(e) => setUpdateData({ ...updateData, relievingLetterIssued: e.target.value === "true" })}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Select>
                  </FormControl>
                  {updateData.relievingLetterIssued && (
                    <FormControl mb={3}>
                      <FormLabel>Relieving Letter Date</FormLabel>
                      <Input
                        type="date"
                        value={updateData.relievingLetterDate}
                        onChange={(e) => setUpdateData({ ...updateData, relievingLetterDate: e.target.value })}
                      />
                    </FormControl>
                  )}
                  <FormControl mb={3}>
                    <FormLabel>Admin Notes</FormLabel>
                    <Textarea
                      value={updateData.adminNotes}
                      onChange={(e) => setUpdateData({ ...updateData, adminNotes: e.target.value })}
                      placeholder="Update notes..."
                      rows={3}
                    />
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button mr={3} onClick={onUpdateClose}>Cancel</Button>
                  <Button bg="#327171" color="white" onClick={handleUpdateExit} _hover={{ bg: "#285e5e" }}>
                    Update
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            {/* Grant Clearance Modal */}
            <Modal isOpen={isClearanceOpen} onClose={onClearanceClose} isCentered>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Grant Clearance</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {selectedRecord && (
                    <Text mb={3}>
                      Grant exit clearance to <strong>{selectedRecord.name || selectedRecord.email}</strong>?
                    </Text>
                  )}
                  <FormControl mb={3}>
                    <FormLabel>Clearance Notes</FormLabel>
                    <Textarea
                      value={clearanceNotes}
                      onChange={(e) => setClearanceNotes(e.target.value)}
                      placeholder="Optional notes..."
                    />
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button mr={3} onClick={onClearanceClose}>Cancel</Button>
                  <Button colorScheme="green" onClick={handleGrantClearance}>
                    Grant Clearance
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}
      </>
    </HrmsPageShell>
  );
};
